<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  export let userId; 

  let userProfile = null;
  let allBusinessPlans = [];
  let activePromotions = [];
  let userAchievements = [];
  let userTransactions = [];
  
  // Form states
  let selectedPlanId = '';
  let paymentIntentId = '';
  let paidAmount = '';
  let selectedPromotionId = '';

  let isLoading = true;
  let isTxnLoading = false;
  let error = '';
  let successMessage = '';

  onMount(async () => {
    await Promise.all([
      fetchUserProfile(), 
      fetchBusinessPlans(),
      fetchActivePromotions(),
      fetchUserAchievements(),
      fetchUserTransactions()
    ]);
    isLoading = false;
  });

  async function fetchUserProfile() {
    try {
      const response = await fetch(getApiUrl(`/users/profile/${userId}`));
      if (!response.ok) throw new Error('User profile not found.');
      userProfile = await response.json();
    } catch (e) { error = e.message; }
  }

  async function fetchBusinessPlans() {
    try {
      const response = await fetch(getApiUrl('/business-plans'));
      if (!response.ok) throw new Error('Could not fetch business plans.');
      allBusinessPlans = await response.json();
    } catch (e) { error = e.message; }
  }

  async function fetchActivePromotions() {
    try {
      const response = await fetch(getApiUrl('/promotions/active?pageSize=100'));
      const data = await response.json();
      activePromotions = data.data.filter(p => p.targetNumTransactions > 0 || p.minimumTargetAccumulatedSpending > 0 || p.maximumTargetNumTransactions > 0 || p.maximumTargetAccumulatedSpending > 0);
    } catch (e) { console.error(e); }
  }

  async function fetchUserAchievements() {
    try {
      const response = await authFetch(getApiUrl(`/achievements/user/${userId}`));
      userAchievements = await response.json();
    } catch (e) { console.error(e); }
  }

  async function fetchUserTransactions() {
    isTxnLoading = true;
    try {
      const response = await authFetch(getApiUrl(`/transactions/public/${userId}?pageSize=50`));
      const data = await response.json();
      userTransactions = data.data || [];
    } catch (e) { console.error(e); }
    isTxnLoading = false;
  }

  async function handleMoveTransaction(txnId, targetAchId) {
    if (!confirm("Are you sure you want to move this transaction? Progress and rewards will be recalculated.")) return;
    
    successMessage = '';
    error = '';
    try {
      const response = await authFetch(getApiUrl('/admin/transactions/move'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: txnId, targetAchievementId: targetAchId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to move transaction.');
      
      successMessage = 'Transaction moved successfully!';
      await Promise.all([fetchUserAchievements(), fetchUserTransactions()]);
    } catch (e) {
      error = e.message;
    }
  }

  async function handleStatusToggle() {
    successMessage = '';
    error = '';
    const newStatus = userProfile.isBusinessAccount;
    try {
      const response = await authFetch(getApiUrl('/admin/users/business-status'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userProfile.userId, isBusiness: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update status.');
      successMessage = 'User business status updated!';
    } catch (e) {
      error = e.message;
      userProfile.isBusinessAccount = !newStatus;
    }
  }

  async function handleJoinChallenge() {
    successMessage = '';
    error = '';
    try {
      const response = await authFetch(getApiUrl('/achievements'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userProfile.userId, promotionId: selectedPromotionId }),
      });
      if (!response.ok) throw new Error('Failed to join challenge.');
      successMessage = 'User joined challenge!';
      selectedPromotionId = '';
      fetchUserAchievements();
    } catch (e) { error = e.message; }
  }

  async function handleAddBusinessPlan() {
    successMessage = '';
    error = '';
    const selectedPlan = allBusinessPlans.find(p => p.id === selectedPlanId);
    try {
      const response = await authFetch(getApiUrl('/admin/users/business-order'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userProfile.userId,
          displayName: userProfile.displayName,
          planId: selectedPlanId,
          paymentIntentId,
          paidAmount: parseFloat(paidAmount),
          paidCurrency: selectedPlan.currency,
        }),
      });
      if (!response.ok) throw new Error('Failed to add plan.');
      successMessage = 'Business plan added!';
      selectedPlanId = ''; paymentIntentId = ''; paidAmount = '';
    } catch (e) { error = e.message; }
  }

  async function handleUnbanUser() {
    if (!confirm($t('admin.users.unban_confirm'))) return;
    try {
      const res = await authFetch(getApiUrl(`/admin/users/${userId}/unban`), {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to unban user');
      userProfile.promoBanUntil = null;
      userProfile.rejectedSpamCount = 0;
      userProfile = userProfile;
      successMessage = $t('admin.users.unbanned_success');
    } catch (e) {
      error = e.message;
    }
  }
</script>

<div class="admin-container">
  <div class="header-actions">
    <button class="back-btn" on:click={() => history.back()}>← Back to List</button>
    <h2>Manage User</h2>
  </div>

  {#if isLoading}
    <p>Loading user details...</p>
  {:else if userProfile}
    <div class="user-card">
      <h3>{userProfile.displayName}</h3>
      <p><strong>User ID:</strong> <code>{userProfile.userId}</code></p>
      <div class="business-status">
        <strong>Business Account Status:</strong>
        <label class="switch">
          <input type="checkbox" bind:checked={userProfile.isBusinessAccount} on:change={handleStatusToggle}>
          <span class="slider round"></span>
        </label>
        <span>{userProfile.isBusinessAccount ? 'Active' : 'Inactive'}</span>
      </div>
    </div>

    <!-- Moderation & Posting Ban Status -->
    <div class="form-section">
      <h3>{$t('admin.users.ban_status')}</h3>
      <div class="ban-status-row">
        <p>
          <strong>{$t('admin.users.ban_status')}:</strong>
          {#if userProfile.promoBanUntil && new Date(userProfile.promoBanUntil) > new Date()}
            <span class="ban-badge danger">
              ⛔ {$t('admin.users.banned_until')} {new Date(userProfile.promoBanUntil).toLocaleString()}
            </span>
            <button class="sm-btn warning" on:click={handleUnbanUser} style="margin-left: 0.8rem;">
              {$t('admin.users.unban')}
            </button>
          {:else}
            <span class="clean-badge">{$t('admin.users.clean_status')}</span>
          {/if}
        </p>
        <p>
          <strong>{$t('admin.users.strikes')}:</strong> {userProfile.rejectedSpamCount || 0}
        </p>
      </div>

      <div style="margin-top: 1rem;">
        <h4>{$t('admin.users.ban_history')}</h4>
        {#if userProfile.banHistory && userProfile.banHistory.length > 0}
          <div class="table-scroll">
            <table class="txn-table">
              <thead>
                <tr>
                  <th>Banned At</th>
                  <th>Until</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {#each [...userProfile.banHistory].reverse() as record}
                  <tr>
                    <td>{new Date(record.bannedAt).toLocaleString()}</td>
                    <td>{new Date(record.bannedUntil).toLocaleString()}</td>
                    <td>{record.reason || 'Spam / low quality violation'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {:else}
          <p class="hint">{$t('admin.users.no_ban_history')}</p>
        {/if}
      </div>
    </div>

    <!-- Join Challenge -->
    <div class="form-section highlight">
      <h3>Join Challenge on Behalf</h3>
      <div class="form-group">
        <select bind:value={selectedPromotionId}>
          <option value="" disabled>-- Select an Active Promotion --</option>
          {#each activePromotions as promo}
            <option value={promo._id}>{promo.name}</option>
          {/each}
        </select>
      </div>
      <button class="join-btn" on:click={handleJoinChallenge} disabled={!selectedPromotionId}>
        Enroll User
      </button>
    </div>

    <!-- NEW SECTION: Transaction Management -->
    <div class="form-section">
      <h3>Transaction History & Reassignment</h3>
      <p class="hint">Move transactions between achievements to correct logging errors.</p>
      
      {#if isTxnLoading}
        <p>Loading transactions...</p>
      {:else if userTransactions.length === 0}
        <p>No transactions found for this user.</p>
      {:else}
        <div class="table-scroll">
          <table class="txn-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Promotion (Current)</th>
                <th>Amount</th>
                <th>Reward</th>
                <th>Move To</th>
              </tr>
            </thead>
            <tbody>
              {#each userTransactions as txn}
                <tr>
                  <td>{new Date(txn.transactionDateTime).toLocaleDateString()}</td>
                  <td><small>{txn.promotionName}</small></td>
                  <td>${(txn.amount ?? 0).toFixed(2)}</td>
                  <td>${(txn.rewardAmount ?? 0).toFixed(2)}</td>
                  <td>
                    <select class="sm-select" on:change={(e) => handleMoveTransaction(txn._id, e.target.value)}>
                      <option value="">-- Reassign --</option>
                      {#each userAchievements.filter(a => a._id !== txn.achievementId) as ach}
                        <option value={ach._id}>{ach.promotionName}</option>
                      {/each}
                    </select>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>

    <!-- Manual Business Plan -->
    <div class="form-section">
      <h3>Manually Add Business Plan</h3>
      <form on:submit|preventDefault={handleAddBusinessPlan}>
        <div class="form-group">
          <select bind:value={selectedPlanId}>
            <option value="" disabled>-- Select a Plan --</option>
            {#each allBusinessPlans as plan}
              <option value={plan.id}>{plan.name}</option>
            {/each}
          </select>
        </div>
        <div class="form-group">
          <input type="text" bind:value={paymentIntentId} placeholder="Payment Reference ID" />
        </div>
        <div class="form-group">
          <input type="number" step="0.01" bind:value={paidAmount} placeholder="Amount Paid" />
        </div>
        <button class="btn-pri" type="submit">Add Plan</button>
      </form>
    </div>

    {#if successMessage}<p class="success-banner">{successMessage}</p>{/if}
    {#if error}<p class="error-banner">{error}</p>{/if}
  {/if}
</div>

<style>
  .admin-container { max-width: 900px; margin: 2rem auto; text-align: left; font-family: sans-serif; }
  .header-actions { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
  .back-btn { background: none; border: none; color: #007bff; cursor: pointer; }
  
  .user-card, .form-section {
    background: #fff; padding: 1.5rem; border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-bottom: 1.5rem;
  }
  .form-section.highlight { border-left: 4px solid #28a745; }
  .hint { font-size: 0.85rem; color: #666; margin-bottom: 1rem; }
  
  .table-scroll { overflow-x: auto; border: 1px solid #eee; border-radius: 4px; }
  .txn-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .txn-table th, .txn-table td { padding: 0.75rem; border-bottom: 1px solid #eee; text-align: left; }
  .txn-table th { background: #f8f9fa; }
  
  .sm-select { padding: 4px; border-radius: 4px; border: 1px solid #ccc; max-width: 150px; font-size: 0.8rem; }
  
  .success-banner { padding: 1rem; background: #d4edda; color: #155724; border-radius: 4px; margin-top: 1rem; }
  .error-banner { padding: 1rem; background: #f8d7da; color: #721c24; border-radius: 4px; margin-top: 1rem; }

  .form-group { margin-bottom: 1rem; }
  input, select { width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  
  button { padding: 0.6rem 1.2rem; border: none; border-radius: 4px; cursor: pointer; font-weight: 500; }
  .join-btn { background-color: #28a745; color: white; width: 100%; }
  .btn-pri { background-color: #007bff; color: white; width: 100%; }

  .sm-btn { 
    padding: 0.35rem 0.75rem; border: 1px solid #ccc; background: #f8f9fa; 
    cursor: pointer; border-radius: 4px; font-size: 0.85rem; display: inline-block;
  }
  .sm-btn.warning { background: #fff3cd; border-color: #ffeeba; color: #856404; font-weight: bold; }

  .ban-badge { background: #ffebe9; color: #cf222e; padding: 2px 8px; border-radius: 12px; font-size: 0.85rem; font-weight: bold; display: inline-block; }
  .clean-badge { color: #28a745; font-weight: bold; font-size: 0.9rem; }
  .ban-status-row { display: flex; flex-direction: column; gap: 0.5rem; }

  .switch { position: relative; display: inline-block; width: 50px; height: 26px; }
  .switch input { opacity: 0; width: 0; height: 0; }
  .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; border-radius: 34px; }
  .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 4px; bottom: 4px; background-color: white; transition: .4s; border-radius: 50%; }
  input:checked + .slider { background-color: #28a745; }
  input:checked + .slider:before { transform: translateX(24px); }
</style>